import { DogFood } from '../../Dogfood/Dogfood';
import { prefetchFlagrFlags } from '../../Dogfood/preload/prefetchFlagrFlags';
import { withPreloader } from '../../UnicronApp/preloader/withPreloader';
import { CookiesProvider } from '../../common/CookiesProvider/CookiesProvider';
import { preloadExperiments } from '../../common/ExperimentContext/preload/preloadExperiments';
import { DesktopTopNavExperiment } from '../../common/Experiments/DesktopTopNavExperiment';
import { SuggestionsExperiment } from '../../common/components/SearchHeader/Suggestions/SuggestionsExperiment/SuggestionsExperiment';
import { SearchFilterUrlProvider } from '../../common/components/SearchHeader/filters/SearchFilterUrlProvider';
import { makeSearchUrl } from '../../common/components/SearchHeader/urls/makeSearchUrl';
import { parseSearchUrl } from '../../common/components/SearchHeader/urls/parseSearchUrl';
import { preloadFlagrEvaluation } from '../../common/flagr/preload/preloadFlagrEvaluation';
import { preloadRedirect } from '../../common/routing/Redirect';
import { preloadHannibalQuery } from '../../common/schema/hannibal/preloadHannibalQuery';
import {
  FreeTextSearchDocument,
  NearbySuburbsDocument,
} from '../../common/schema/hannibal/types.generated';
import { PromiseBag } from '../../common/util/PromiseBag';
import { env } from '../../common/util/env';
import { clientIP, officeIPFilter } from '../../common/util/officeIPFilter';
import { proxyToHost } from '../../common/util/proxyToHost';
import { run } from '../../common/util/run';
import { BrazeWrapper } from '../../common/vendors/braze/BrazeWrapper';
import { AlertBoxExperiment } from '../AlertBar/AlertBoxExperiment';
import { CovidSpecialtyMessageExperiment } from '../CovidSpecialtyMessage/CovidSpecialtyMessageExperiment';
import { PracticeCardExperiment } from '../ResultView/ResultList/ProfileCards/search-card/PracticeCard/PracticeCardExperiment/PracticeCardExperiment';
import { FLAGR_TELEHEALTH_GP_SEARCH_CARDS } from '../ResultView/ResultList/TelehealthActions/TelehealthActions';
import { DEFAULT_SEARCH_RADIUS } from '../SearchPage/DEFAULT_SEARCH_RADIUS';
import { SearchPage } from '../SearchPage/SearchPage';
import { extendSearch } from '../SearchPage/extendSearch';
import { isExtendedSearchPossible } from '../SearchPage/isExtendedSearchPossible';
import { FLAGR_MAP_SEARCH } from '../constants';
import { isRedirectNeeded } from '../isRedirectNeeded';
import { SeoContentProvider } from '../page-seo/SeoContentProvider';
import { makeSearchVariables } from '../useSearchQuery/useVariables';

const PRELOADED_FLAGS = [FLAGR_MAP_SEARCH, FLAGR_TELEHEALTH_GP_SEARCH_CARDS];

export const SearchEntrypoint = withPreloader(
  () => (
    <BrazeWrapper>
      <CookiesProvider>
        <SearchFilterUrlProvider>
          <SeoContentProvider>
            <SearchPage />
            <DogFood />
          </SeoContentProvider>
        </SearchFilterUrlProvider>
      </CookiesProvider>
    </BrazeWrapper>
  ),

  async job => {
    const { server, context, services } = job;
    const { flags, logger, serverTimestamp } = services;

    if (flags.oldSearch && server) {
      return await proxyToHost(job, env.MEGATRON_CLUSTER_DOMAIN, env.MEGATRON_PROTOCOL);
    }

    const filters = parseSearchUrl(context.asPath ?? '', context.query);

    if (server) {
      const redirectNeeded = isRedirectNeeded(context.asPath ?? '', logger);

      if (redirectNeeded) {
        preloadRedirect(job, makeSearchUrl(filters));
      }
    }

    const promises = new PromiseBag();

    if (filters.location?.suburb) {
      promises.add(
        preloadHannibalQuery(job, NearbySuburbsDocument, {
          state: filters.location.suburb.state,
          postcode: filters.location.suburb.postcode,
          name: filters.location.suburb.name ?? '',
        }),
      );
    }

    if (job.server) {
      promises.add(
        run(async () => {
          const searchQueryResult = await preloadHannibalQuery(
            job,
            FreeTextSearchDocument,
            makeSearchVariables({
              filterChoice: filters,
              frozenNow: serverTimestamp,
              searchRadius: DEFAULT_SEARCH_RADIUS,
            }),
          );

          const extendedSearchNeeded = isExtendedSearchPossible(
            searchQueryResult.data?.searchProfiles.totalCount,
            filters,
          );

          if (extendedSearchNeeded) {
            await preloadHannibalQuery(
              job,
              FreeTextSearchDocument,
              makeSearchVariables({
                filterChoice: extendSearch(filters),
                frozenNow: serverTimestamp,
              }),
            );
          }
        }),
      );
    }

    if (
      process.env.NODE_ENV === 'development' ||
      (officeIPFilter(context.req, logger) && job.server)
    ) {
      promises.add(prefetchFlagrFlags(job));
    }

    if (!flags.disableExperiments) {
      const ip = context.req ? clientIP(context.req) ?? 'unknown_ip' : 'unknown_ip';
      promises.add(preloadFlagrEvaluation(job, PRELOADED_FLAGS, [ip]));

      promises.add(
        preloadExperiments(job, [
          AlertBoxExperiment,
          CovidSpecialtyMessageExperiment,
          PracticeCardExperiment(),
          DesktopTopNavExperiment,
          SuggestionsExperiment(true),
        ]),
      );
    }

    await promises.allDone();
  },
);
